// gitAccess.mjs — /admin/git-access 的纯逻辑（第一百三十五批：发放可改走定向密送）。
//
// 为什么这件事值一只纯函数（与 apiKeys.mjs 的 issueResultKind 同一条理由）：
// `'token' in resp` 与 `resp.token` 的真假在页面上长得一模一样，而错了的后果是把
// 一个**可以复制的空框**摆在管理员面前——他会以为自己复制到了什么，然后把空剪贴板
// 发过去，而那枚令牌已经在密件里、72 小时后过期。后端契约（C.7 同款）：递送时
// `token` 这个键**整个不出现**，不是空串。
//   'delivered' —— 递成了密件（显示密件号+去处，绝不显示明文，因为没有）
//   'plaintext' —— 常规签发，明文只此一次
//   'none'      —— 两者都没有：不是"空"，是**这次签发的结果说不清**，当异常提示，
//                  绝不能悄悄渲染成一个空的复制框。
export function gitIssueResultKind(resp) {
  if (!resp || typeof resp !== 'object') return 'none';
  if (resp.delivery && typeof resp.delivery === 'object' && resp.delivery.id != null) {
    return 'delivered';
  }
  return typeof resp.token === 'string' && resp.token.length > 0 ? 'plaintext' : 'none';
}

// 「这个账号没有组，密件没有落点」要在**点之前**说（AdminApiKeysPage 同一条界面
// 纪律：后端对无组也回 409 CONFLICT，与「用户名已持凭据」同码，事后按码分不开，
// 而两句话的下一步动作完全相反）。判据抽到这里是为了可穷举单测——数组缺席/空数组/
// 非数组三态都落「无组」侧（fail-closed：判不出有组就当无组，宁可让管理员多看一眼
// 账号页，不放一次注定 409 的勾选）。
export function accountHasGroup(account) {
  return Boolean(account) && Array.isArray(account.projectGroupIds)
    && account.projectGroupIds.length > 0;
}

// ══ git 凭据体系改造（内外双线裁定批）：凭据/授权两级视图的纯逻辑 ══════════════
//
// 页面从「一行=一仓一凭据」换成「一枚凭据下挂多条 grant」，随之而来的是三类
// 只能靠约定活着的东西：响应整形（后端字段缺席/形态不对时页面不该白屏）、
// 布尔位的 fail-closed 折算（canWrite 判不清就当只读）、表单的"点之前说清"。
// 全部抽到这里，逐条咬死。相对时间**不重写**——lastSeen.mjs 的 relativeSince
// 已把取整边界全部单测过，第二份实现就是第二份漂移面。
import { relativeSince } from './lastSeen.mjs';
// 第二百零七批（SB-180 (e)）：密送落点组的判断在 deliverGroup.mjs 一处定形，
// 两个发放面共用——这里只把它编进 issueBlocker 的拦截链。
import { deliveryToGroupId, needsGroupChoice } from './deliverGroup.mjs';

// GET /api/admin/git-repos 的 {repos:[{repo, enabledGrants}]} → 下拉数据。
// 认不出的行整行丢弃（一个没有 repo 名的选项选了也发不出去）；enabledGrants
// 折算成非负整数——它只是提示位，坏值宁可显示 0 也不显示 NaN。
export function repoOptions(data) {
  const raw = Array.isArray(data?.repos) ? data.repos : [];
  return raw
    .filter((r) => r && typeof r === 'object' && typeof r.repo === 'string' && r.repo.length > 0)
    .map((r) => {
      const n = Number(r.enabledGrants);
      return { repo: r.repo, enabledGrants: Number.isFinite(n) && n > 0 ? Math.floor(n) : 0 };
    });
}

// 一枚凭据的 grants → 子表行。三条纪律：
//   · 布尔位 **=== true 才算真**（fail-closed：后端将来多一种值，页面把它读成
//     "可写"是在替一枚不该写的凭据背书，读成"只读"最多是少显示一点权限）；
//   · 没有 id 或 repo 的行丢弃（行内动作全都要拿 id 说话，没 id 的行按不动）；
//   · 生效的排前面、同态按仓名序——吊销留行的表里，最要紧的是"现在能进哪些仓"。
export function credentialGrants(cred) {
  const raw = Array.isArray(cred?.grants) ? cred.grants : [];
  return raw
    .filter((g) => g && typeof g === 'object' && g.id != null
      && typeof g.repo === 'string' && g.repo.length > 0)
    .map((g) => ({
      id: g.id, repo: g.repo,
      canWrite: g.canWrite === true,
      enabled: g.enabled === true,
    }))
    .sort((a, b) => (Number(b.enabled) - Number(a.enabled))
      || (a.repo < b.repo ? -1 : a.repo > b.repo ? 1 : 0));
}

// GET /api/admin/git-credentials 的响应 → 页面行。整形一次、页面只读干净值。
// useCount 与 enabledGrants 同一条折算纪律；lastUsedAtMs 保持原样交给
// relativeSince（它自己把 0/null/垃圾折成"从未"）。
export function credentialRows(data) {
  const raw = Array.isArray(data?.entries) ? data.entries : []  // 后端键名 entries（与旧面同形制）;
  return raw
    .filter((c) => c && typeof c === 'object' && c.id != null
      && typeof c.username === 'string' && c.username.length > 0)
    .map((c) => {
      const n = Number(c.useCount);
      return {
        id: c.id,
        username: c.username,
        label: typeof c.label === 'string' ? c.label : '',
        enabled: c.enabled === true,
        isService: c.isService === true,
        accountId: c.accountId ?? null,
        lastUsedAtMs: c.lastUsedAtMs ?? null,
        useCount: Number.isFinite(n) && n > 0 ? Math.floor(n) : 0,
        grants: credentialGrants(c),
      };
    });
}

// 「这枚凭据还能加哪些仓的 grant」= 仓单 − 已有**生效** grant 的仓。
// 已吊销的 grant 不占位：吊销留行只是台账，重新授权同一个仓是合法动作，
// 把它从下拉里划掉等于把"吊销后想重开"这条路在页面上焊死。
export function grantableRepos(repos, cred) {
  const held = new Set(credentialGrants(cred).filter((g) => g.enabled).map((g) => g.repo));
  return (Array.isArray(repos) ? repos : [])
    .map((r) => (r && typeof r === 'object' ? r.repo : r))
    .filter((name) => typeof name === 'string' && name.length > 0 && !held.has(name));
}

// 使用记录列：{ rel: {key,vars}|null, count }。rel=null 渲染"从未使用"。
// rel 与 count 各自独立折算——一个 lastUsed 为空但 count>0 的行是后端的数据病，
// 页面把两个值都如实端出来，比替它圆谎（隐藏其一）更容易被发现。
export function usageView(cred, nowMs) {
  const n = Number(cred?.useCount);
  return {
    rel: relativeSince(cred?.lastUsedAtMs, nowMs),
    count: Number.isFinite(n) && n > 0 ? Math.floor(n) : 0,
  };
}

// 发放表单的"为什么还不能点"——返回第一个拦住它的原因，null = 可发。
// 枚举返回值而不是布尔，是因为每个原因对应页面上一句**不同的话**（AdminApiKeysPage
// 的 noGroup 同一条纪律：要在点之前说清，而不是让人点一次再猜）。
//   'username'  用户名空
//   'repo'      一个仓都没选（含仓单为空——那时下拉本身就是禁用的）。发放即多选
//               （第一百七十五批）：仍要求至少一仓——裸凭据什么也进不去，密送信
//               也会没有真仓单可列；表单不给"发一枚空凭据"这条路。
//   'recipient' 勾了密送但没选归属账号（密件是指名递送的）
//   'noGroup'   勾了密送、选了账号、但该账号无组（密件没有落点）
//   'group'     第二百零七批（SB-180 (e) 实撞）：账号多于一组、落点组还没选定。
//               后端此前自动取首组落密件——多组收件人落错组正是撞过的那个形状，
//               现在多组必须显式选，单组不拦（唯一落点没有选错的可能）。
// repos 收数组（多选下拉的值域）；非数组/缺席按空数组算（fail-closed 落 'repo'）。
export function issueBlocker({ username, repos, deliver, accountId, account, toGroupId } = {}) {
  if (!String(username ?? '').trim()) return 'username';
  const list = Array.isArray(repos) ? repos.filter((r) => String(r ?? '').trim()) : [];
  if (list.length === 0) return 'repo';
  if (deliver) {
    if (!accountId) return 'recipient';
    if (!accountHasGroup(account)) return 'noGroup';
    if (needsGroupChoice(account) && deliveryToGroupId(account, toGroupId) == null) return 'group';
  }
  return null;
}

// 「发送用法介绍信」按钮的"为什么还不能点"（第一百七十五批）——与后端拒绝面
// 同一套判据（凭据吊销/未绑定/零生效授权），在点之前用人话说清；null = 可发。
// 服务端仍是闸：这里判得过、后端刚好变了状态 ⇒ 后端的 4xx 原话兜底。
//   'revoked'   凭据已吊销（对着尸体教用法）
//   'unbound'   没绑归属账号（介绍信没有收件人可推导）
//   'noGrants'  没有任何生效授权（信里没有仓可列）
export function introBlocker(cred) {
  if (!cred || cred.enabled !== true) return 'revoked';
  if (cred.accountId == null) return 'unbound';
  const grants = credentialGrants(cred);
  if (!grants.some((g) => g.enabled)) return 'noGrants';
  return null;
}
